import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import type * as React from "react";
import { Box, Divider, GlobalStyles, Typography } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import ReceiptDownloadLineIcon from "assets/icons/icon-download-line.png";
import HeaderLogo from "assets/images/GL-Logo.svg";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import AccountExitLink from "components/v3/sharedComponents/AccountExitLink";
import GLButton from "components/v3/sharedComponents/GLButton";
import { formatUsdWithSpaceAfterSymbol } from "components/v3/sharedComponents/AnimatedUsdAmount";
import { COLORS } from "components/v3/Theme/colors";
import {
  GENOMIC_LIFE_COMPLETE_MEMBER_FEATURES,
  GENOMIC_LIFE_COMPLETE_MEMBER_INTRO
} from "constants/genomicLifeCompleteProgramCopy";
import { PATHS } from "constants/pathConstants";
import PageTitle from "utils/PageTitle";
import GiftMembershipPurchaseThankYouModal from "components/v3/pages/account/GiftMembershipPurchaseThankYouModal";

export type PaymentReceiptLocationState = {
  totalUsd: number;
  quantity: number;
  membershipRenewed?: boolean;
  giftMembershipPurchased?: boolean;
};

function formatReceiptIssuedAt(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(date);
}

const receiptCardSx = {
  bgcolor: COLORS.BACKGROUND_BLUE,
  borderRadius: "12px",
  boxSizing: "border-box" as const,
  maxWidth: "100%",
  p: { md: 3, xs: 2.5 },
  width: "100%",
  "@media print": {
    bgcolor: "transparent",
    boxShadow: "none",
    breakInside: "avoid" as const,
    p: 1.25
  }
};

const receiptDividerSx = {
  borderBottomWidth: "3px",
  borderColor: COLORS.PRIMARY_LIGHTEST,
  my: 2,
  "@media print": {
    borderBottomWidth: "2px",
    my: 1
  }
} as const;

const receiptPrintLayoutRootSx = {
  "@media print": {
    background: "#fff",
    minHeight: "auto",
    height: "auto",
    "& > *:not(main)": {
      display: "none !important"
    },
    "& > main": {
      alignItems: "stretch",
      width: "100%"
    }
  }
} as const;

const receiptDownloadIconMaskSx = {
  backgroundColor: COLORS.PRIMARY,
  flexShrink: 0,
  height: 22,
  maskImage: `url(${ReceiptDownloadLineIcon})`,
  maskPosition: "center",
  maskRepeat: "no-repeat",
  maskSize: "contain",
  WebkitMaskImage: `url(${ReceiptDownloadLineIcon})`,
  WebkitMaskPosition: "center",
  WebkitMaskRepeat: "no-repeat",
  WebkitMaskSize: "contain",
  width: 22
} as const;

const PaymentReceiptPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const routeState = location.state as PaymentReceiptLocationState | undefined;
  const [giftThankYouModalOpen, setGiftThankYouModalOpen] = useState(
    routeState?.giftMembershipPurchased === true
  );

  const receiptIssuedAt = useMemo(() => new Date(), []);

  const closeGiftThankYouModal = useCallback(() => {
    setGiftThankYouModalOpen(false);
    if (routeState?.giftMembershipPurchased) {
      const { giftMembershipPurchased: _removed, ...rest } = routeState;
      history.replace(location.pathname, rest);
    }
  }, [history, location.pathname, routeState]);

  useEffect(() => {
    const q = routeState?.quantity;
    const total = routeState?.totalUsd;
    if (
      total == null ||
      q == null ||
      Number.isNaN(total) ||
      Number.isNaN(q) ||
      q < 1 ||
      total <= 0
    ) {
      history.replace(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION);
    }
  }, [history, routeState]);

  const exitHref = PATHS.ACCOUNT_MANAGEMENT_PAYMENT_INFORMATION;
  const exitState =
    routeState?.membershipRenewed === true
      ? { membershipRenewed: true }
      : undefined;

  const handleDownload = useCallback(() => {
    globalThis.print();
  }, []);

  PageTitle("Receipt");

  if (
    routeState?.totalUsd == null ||
    routeState.quantity == null ||
    routeState.quantity < 1 ||
    routeState.totalUsd <= 0
  ) {
    return null;
  }

  const { totalUsd, quantity } = routeState;
  const amountLabel = formatUsdWithSpaceAfterSymbol(totalUsd);
  const issuedLabel = formatReceiptIssuedAt(receiptIssuedAt);

  const paymentDetailRows = [
    { emphasizeValue: false, label: "Payment Method", value: "Credit card" },
    { emphasizeValue: false, label: "Payment Date", value: issuedLabel },
    { emphasizeValue: true, label: "Amount Paid", value: amountLabel }
  ];

  return (
    <>
      <GiftMembershipPurchaseThankYouModal
        open={giftThankYouModalOpen}
        onClose={closeGiftThankYouModal}
      />
      <GlobalStyles styles="@page { margin: 8mm 10mm; }" />
      <GradientFramedLayout
        rootSx={receiptPrintLayoutRootSx}
        showFooter
        showHeader
        showNav
      >
        <Box
          sx={{
            alignSelf: "stretch",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            marginX: "auto",
            maxWidth: { md: 720, xs: "100%" },
            minWidth: 0,
            pb: { md: 5, xs: 4 },
            pt: { md: 2, xs: 2 },
            px: { md: 3, xs: 2 },
            width: "100%",
            "@media print": {
              gap: 1,
              maxWidth: "100%",
              pb: 1,
              pt: 0.5,
              px: 2
            }
          }}
        >
          <AccountExitLink
            to={{ pathname: exitHref, state: exitState }}
            sx={{
              alignSelf: "flex-start",
              mb: 2,
              "@media print": {
                display: "none"
              }
            }}
          />

          <Box
            alt=""
            component="img"
            src={HeaderLogo}
            sx={{
              alignSelf: "flex-start",
              display: "block",
              height: "auto",
              maxWidth: { xs: 120 },
              width: "100%",
              "@media print": {
                maxWidth: 88,
                mb: 0.25
              }
            }}
          />

          <Box
            sx={{
              alignItems: { sm: "flex-end", xs: "stretch" },
              display: "flex",
              flexDirection: { sm: "row", xs: "column" },
              gap: 2,
              justifyContent: "space-between",
              mb: 2,
              width: "100%",
              "@media print": {
                gap: 1,
                mb: 0.25
              }
            }}
          >
            <Typography
              variant="h4_primary"
              sx={{
                "@media print": {
                  fontSize: "1.125rem",
                  lineHeight: 1.25
                },
                m: 0
              }}
            >
              Receipt From Genomic Life
            </Typography>
            <GLButton
              buttonType="secondary-transparent"
              leftIcon={<Box aria-hidden sx={receiptDownloadIconMaskSx} />}
              onClick={handleDownload}
              sx={{
                alignSelf: { sm: "auto", xs: "stretch" },
                flexShrink: 0,
                maxWidth: { sm: "max-content", xs: "100%" },
                width: { sm: "fit-content", xs: "100%" },
                "@media print": {
                  display: "none"
                }
              }}
              text="Download"
            />
          </Box>

          <Box
            sx={[
              receiptCardSx,
              {
                "@media print": {
                  "& .MuiTypography-root": {
                    fontSize: "0.74rem",
                    lineHeight: 1.28
                  }
                }
              }
            ]}
          >
            <Box
              sx={{
                display: { md: "none", xs: "flex" },
                flexDirection: "column"
              }}
            >
              {paymentDetailRows.map((row, index) => (
                <Fragment key={row.label}>
                  <Box>
                    <Typography
                      variant="body_strong_dark_gray"
                      sx={{ mb: 0.75, m: 0 }}
                    >
                      {row.label}
                    </Typography>
                    <Typography
                      variant="body_strong_dark_gray_2"
                      sx={{
                        fontVariantNumeric: row.emphasizeValue
                          ? ("tabular-nums" as const)
                          : undefined,
                        fontWeight: row.emphasizeValue ? 700 : undefined,
                        m: 0
                      }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                  {index < paymentDetailRows.length - 1 ? (
                    <Divider sx={{ ...receiptDividerSx }} />
                  ) : null}
                </Fragment>
              ))}
            </Box>

            <Box sx={{ display: { md: "block", xs: "none" } }}>
              <Box
                sx={{
                  display: "grid",
                  gap: 0,
                  gridTemplateColumns: "repeat(3, 1fr)"
                }}
              >
                {paymentDetailRows.map((row) => (
                  <Typography
                    key={`hdr-${row.label}`}
                    variant="body_strong_dark_gray"
                    sx={{ m: 0 }}
                  >
                    {row.label}
                  </Typography>
                ))}
              </Box>
              <Divider sx={{ ...receiptDividerSx }} />
              <Box
                sx={{
                  display: "grid",
                  gap: 0,
                  gridTemplateColumns: "repeat(3, 1fr)"
                }}
              >
                {paymentDetailRows.map((row) => (
                  <Typography
                    key={`val-${row.label}`}
                    variant="body"
                    sx={{
                      fontVariantNumeric: row.emphasizeValue
                        ? ("tabular-nums" as const)
                        : undefined,

                      m: 0
                    }}
                  >
                    {row.value}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={receiptCardSx}>
            <Typography
              component="h2"
              sx={{
                color: COLORS.BLACK,
                fontSize: "1.0625rem",
                fontWeight: 700,
                mb: 2,
                "@media print": {
                  fontSize: "0.9rem",
                  mb: 0.5
                }
              }}
            >
              Summary
            </Typography>
            <Typography
              sx={{
                color: COLORS.BLACK,
                fontSize: "1rem",
                fontWeight: 700,
                lineHeight: 1.5,
                mb: 2,
                m: 0,
                "@media print": {
                  fontSize: "0.78rem",
                  lineHeight: 1.35,
                  mb: 0.75
                }
              }}
            >
              Genomic Life Complete Membership Program
              {quantity > 1 ? ` (${quantity} memberships)` : ""}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                "@media print": {
                  gap: 0.35,
                  "& .MuiTypography-root": {
                    fontSize: "0.62rem",
                    lineHeight: 1.3
                  }
                }
              }}
            >
              <Typography
                component="p"
                sx={{
                  color: COLORS.DARK_GRAY_2,
                  lineHeight: 1.65,
                  m: 0
                }}
                variant="body2"
              >
                {GENOMIC_LIFE_COMPLETE_MEMBER_INTRO}
              </Typography>
              {GENOMIC_LIFE_COMPLETE_MEMBER_FEATURES.map((feature) => (
                <Typography
                  key={feature.label}
                  component="p"
                  sx={{
                    color: COLORS.DARK_GRAY_2,
                    lineHeight: 1.65,
                    m: 0
                  }}
                  variant="body2"
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {feature.label}
                  </Box>{" "}
                  {feature.body}
                </Typography>
              ))}
            </Box>

            <Divider sx={{ ...receiptDividerSx }} />

            <Box
              sx={{
                alignItems: "baseline",
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "space-between",
                "@media print": {
                  gap: 1,
                  mt: 0.25
                }
              }}
            >
              <Typography variant="body_strong_dark_gray" sx={{ m: 0 }}>
                Amount Paid
              </Typography>
              <Typography
                variant="body_strong_lg_accent2"
                sx={{
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 700,
                  m: 0,
                  "@media print": {
                    fontSize: "1rem",
                    lineHeight: 1.2
                  }
                }}
              >
                {amountLabel}
              </Typography>
            </Box>
          </Box>
        </Box>
      </GradientFramedLayout>
    </>
  );
};

export default PaymentReceiptPage;
